import type { CreatureArchetype, CreatureAttack, CreatureType, SkillShape } from "@eldrun/content";
import type { CreatureAttackEvent } from "@eldrun/protocol";
import { strikeImpactAt } from "./creature-strike.ts";
import { SHARD_DRAG, SHARD_POOL, type ShardBurst } from "./shards.ts";
import { FLAME_LIFE } from "./smoke.ts";
import type { PieceSpec } from "./skill-pieces.ts";

/**
 * Assinatura dos inimigos maiores (F91): o que cada ataque deles solta além do aviso, do clipe e da voz.
 * Desde a F93, também as chamas dos ataques de fogo do Boitatá; desde a F94, a onda de choque no pós (Alto) nas
 * pancadas e nos golpes pesados de chefe.
 * Tudo nos sistemas que já ficam compilados na partida (estilhaços, as peças da F78, o tremor e o `fumaca`), sem
 * programa novo. A onda no chão é de estilhaços, não o `pulse`: o programa da zona de habilidade só
 * compila no primeiro crítico ou habilidade, e com ele o `budgets` contava um programa a mais que o `main`.
 * A tabela e o plano são puros (sem cena), então os testes leem os passos direto.
 */

/** Quando o passo acontece: no começo do preparo, no acerto (o fim do preparo) ou no pouso do salto. */
export type FxWhen = "preparo" | "impacto" | "pouso";

interface RowBase {
  when: FxWhen;
  /** Depois do instante do `when` (ms). */
  delayMs?: number;
}

/**
 * Estilhaços em pontos de uma forma: no arco (`spread` da abertura), ao longo da faixa, num anel em volta da
 * origem, no centro (a origem, ou à frente dela por `reach`) ou num anel em volta do fim da faixa (`fim`).
 */
export interface ShardsRow extends RowBase {
  kind: "estilhacos";
  along: "arco" | "linha" | "anel" | "centro" | "fim";
  points: number;
  /** Fração do alcance no primeiro e no último ponto (`radius`, em metros, vale no lugar dela). */
  reach: number;
  reachEnd?: number;
  radius?: number;
  /** Altura acima do chão no primeiro e no último ponto (m). */
  lift: number;
  liftEnd?: number;
  /** Fração da abertura do arco coberta pelos pontos (padrão: o arco inteiro). */
  spread?: number;
  /** Entre um ponto e o seguinte (ms): o golpe varre. Na faixa do salto, os pontos seguem o bote. */
  staggerMs?: number;
  /** O jorro de cada ponto (`Shards.burst`). */
  n: number;
  speed: number;
  size: number;
  life: number;
  up?: number;
  /** Arrasto (m/s): para fora (do centro, ou para o lado na faixa) e ao longo da varrida (na faixa, para a frente). */
  out?: number;
  sweep?: number;
  /** Na faixa, o desvio para os lados (fração da meia-largura), alternando. */
  side?: number;
  /** Estalo aceso: um estilhaço parado acima de 1 que some em 0,1 s. Sai com "Reduzir lampejos". */
  flash?: boolean;
}

/**
 * Onda no chão em estilhaços, na forma das marcas do `pulse` (`shape`: círculo, ou polígono de 3, 4 ou 6
 * lados): `points` estilhaços parados no contorno, a `from` m da origem (ou do fim da faixa), que abrem até
 * o raio na vida deles, pela velocidade somada ao jorro e o arrasto dos estilhaços.
 */
export interface WaveRow extends RowBase {
  kind: "onda";
  at: "origem" | "fim";
  shape: SkillShape;
  points: number;
  /** Raio final em metros, ou fração do alcance. */
  radius?: number;
  reach?: number;
  from: number;
  lift: number;
  size: number;
  life: number;
}

/** Anel de cristais que brotam do chão (as peças da F78): prisma e lança alternados, um depois do outro. */
export interface CrystalsRow extends RowBase {
  kind: "cristais";
  count: number;
  /** Raio do anel, em fração do alcance. */
  reach: number;
  staggerMs: number;
  /** Quanto cada cristal sobe e de quão fundo ele sai (m). */
  rise: number;
  depth: number;
  ms: number;
  glow: number;
  fill: number;
}

/** Tremor de câmera pela distância (`CameraShake.falloff`): trauma e o raio onde zera. */
export interface ShakeRow extends RowBase {
  kind: "tremor";
  at: "origem" | "fim";
  amount: number;
  radius: number;
}

/**
 * Chamas (F93) no pool do `fumaca`: no centro (a marca), num anel em volta dele ou espalhadas pelo cone do
 * arco (o sopro), de `reach` a `reachEnd` do alcance, um ponto a cada `staggerMs` (o sopro varre).
 */
export interface FlamesRow extends RowBase {
  kind: "chamas";
  along: "centro" | "anel" | "arco";
  points: number;
  reach: number;
  reachEnd?: number;
  /** Fração da abertura do arco coberta pelos pontos (padrão: o arco inteiro). */
  spread?: number;
  staggerMs?: number;
  /** Lambidas por ponto, quanto elas se espalham (m) e a largura de cada língua (m). */
  n: number;
  scatter: number;
  size: number;
}

/**
 * Onda de choque no pós (F94, só no Alto): no impacto, na origem, no meio do alcance à frente (o arco) ou no fim
 * da faixa, `lift` m acima do chão, com a força (0 a 1) e o raio final em metros, ou em fração do alcance.
 */
export interface ShockRow extends RowBase {
  kind: "choque";
  at: "origem" | "meio" | "fim";
  strength: number;
  radius?: number;
  reach?: number;
  lift: number;
}

export type CreatureFxRow = ShardsRow | WaveRow | CrystalsRow | ShakeRow | FlamesRow | ShockRow;

/** Lados do contorno da onda por marca do `pulse` (`SHAPE_MARKS`; o círculo, 0). */
export const WAVE_SIDES: Readonly<Record<SkillShape, number>> = { circulos: 0, triangulos: 3, linhas: 4, hexagonos: 6 };

/** O estalo aceso do golpe (some com "Reduzir lampejos"). */
const flash = (when: FxWhen, reach: number, lift: number, size: number, delayMs = 0): ShardsRow => ({ kind: "estilhacos", when, delayMs, along: "centro", points: 1, reach, lift, n: 1, speed: 0, size, life: 0.1, flash: true });

/**
 * A tabela por `tipo:ataque`. Os três inimigos maiores (F89) e o fogo do Boitatá (F93); as outras criaturas seguem como antes.
 * - Lobisomem Alfa: a mordida solta um crescente no arco; o salto levanta poeira no preparo, deixa estilhaços
 *   ao longo da faixa durante o bote e uma onda de linhas (um losango) com um anel de poeira onde cai.
 * - Esqueleto Colosso: a garra é um corte de cima para baixo no arco; a pancada é a onda de círculos do
 *   tamanho da área (duas, uma dentro da outra), dois anéis de estilhaços pelo chão, o tremor e a onda de choque (F94).
 * - Troll de Pedra: a pancada faz brotar um anel de cristais, com a onda de hexágonos, o entulho, o tremor e a onda
 *   de choque (F94);
 *   o agarrão solta uma rajada para fora no arco.
 * - Boitatá (F93): o cuspe, a chuva e o rastro acendem chamas onde cada marca cai; o sopro espalha chamas
 *   pelo cone enquanto bate (8 pulsos em 2 s).
 */
export const CREATURE_FX: Readonly<Record<string, readonly CreatureFxRow[]>> = {
  "lobisomem-alfa:mordida": [
    { kind: "estilhacos", when: "impacto", along: "arco", points: 7, reach: 0.95, lift: 1.1, liftEnd: 0.7, staggerMs: 10, n: 3, speed: 1.4, size: 0.1, life: 0.4, up: 0.3, sweep: 3.5, out: 1 },
    flash("impacto", 0.7, 1, 0.4),
  ],
  "lobisomem-alfa:salto": [
    { kind: "estilhacos", when: "preparo", delayMs: 350, along: "centro", points: 1, reach: 0, lift: 0.15, n: 8, speed: 2.2, size: 0.08, life: 0.5, up: 0.5 },
    { kind: "estilhacos", when: "impacto", along: "linha", points: 8, reach: 0.05, reachEnd: 1, lift: 0.25, side: 0.5, n: 3, speed: 1.4, size: 0.1, life: 0.55, up: 0.6, sweep: -2.5, out: 1.2 },
    { kind: "onda", when: "pouso", at: "fim", shape: "linhas", points: 16, radius: 2.4, from: 0.4, lift: 0.08, size: 0.09, life: 0.45 },
    { kind: "estilhacos", when: "pouso", along: "fim", points: 8, reach: 0, radius: 0.9, lift: 0.15, n: 2, speed: 1.8, size: 0.11, life: 0.6, up: 0.5, out: 3 },
  ],
  "corpo-seco-colosso:garra": [
    { kind: "estilhacos", when: "impacto", along: "arco", points: 9, reach: 0.65, reachEnd: 1, lift: 2.4, liftEnd: 0.3, staggerMs: 14, n: 3, speed: 1.2, size: 0.13, life: 0.45, up: 0.2, sweep: 4.5, out: 0.8 },
    flash("impacto", 0.85, 0.6, 0.5, 120),
  ],
  "corpo-seco-colosso:pancada": [
    { kind: "onda", when: "impacto", at: "origem", shape: "circulos", points: 32, reach: 1, from: 0.8, lift: 0.08, size: 0.11, life: 0.55 },
    { kind: "onda", when: "impacto", delayMs: 110, at: "origem", shape: "circulos", points: 24, reach: 0.6, from: 0.5, lift: 0.08, size: 0.09, life: 0.5 },
    { kind: "estilhacos", when: "impacto", along: "anel", points: 16, reach: 0.45, lift: 0.1, n: 3, speed: 2.4, size: 0.14, life: 0.75, up: 0.6, out: 4.5 },
    { kind: "estilhacos", when: "impacto", along: "anel", points: 10, reach: 0.15, lift: 0.1, n: 2, speed: 3, size: 0.16, life: 0.6, up: 0.85, out: 2 },
    flash("impacto", 0, 0.4, 0.8),
    { kind: "tremor", when: "impacto", at: "origem", amount: 0.45, radius: 22 },
    { kind: "choque", when: "impacto", at: "origem", strength: 0.8, reach: 1, lift: 0.3 },
  ],
  "capelobo-ancestral:pancada": [
    { kind: "cristais", when: "impacto", count: 8, reach: 0.5, staggerMs: 30, rise: 1.3, depth: 1.1, ms: 1100, glow: 2.6, fill: 0.22 },
    { kind: "onda", when: "impacto", at: "origem", shape: "hexagonos", points: 36, reach: 1, from: 0.9, lift: 0.08, size: 0.11, life: 0.6 },
    { kind: "estilhacos", when: "impacto", along: "anel", points: 12, reach: 0.2, lift: 0.1, n: 3, speed: 2.6, size: 0.15, life: 0.7, up: 0.7, out: 3.5 },
    flash("impacto", 0, 0.4, 0.8),
    { kind: "tremor", when: "impacto", at: "origem", amount: 0.5, radius: 24 },
    { kind: "choque", when: "impacto", at: "origem", strength: 0.9, reach: 1, lift: 0.3 },
  ],
  "boitata:cuspe": [
    { kind: "chamas", when: "impacto", along: "centro", points: 1, reach: 0, n: 7, scatter: 1.1, size: 1 },
    { kind: "chamas", when: "impacto", delayMs: 60, along: "anel", points: 8, reach: 0.75, n: 1, scatter: 0.3, size: 0.8 },
  ],
  "boitata:chuva": [
    { kind: "chamas", when: "impacto", along: "centro", points: 1, reach: 0, n: 5, scatter: 0.9, size: 0.9 },
    { kind: "chamas", when: "impacto", delayMs: 60, along: "anel", points: 6, reach: 0.7, n: 1, scatter: 0.3, size: 0.7 },
  ],
  "boitata:rastro": [{ kind: "chamas", when: "impacto", along: "centro", points: 1, reach: 0, n: 5, scatter: 0.9, size: 0.9 }],
  "boitata:sopro": [{ kind: "chamas", when: "impacto", along: "arco", points: 24, reach: 0.15, reachEnd: 1, spread: 0.8, staggerMs: 80, n: 2, scatter: 0.5, size: 0.9 }],
  "capelobo-ancestral:agarrao": [
    { kind: "estilhacos", when: "impacto", along: "arco", points: 7, reach: 0.55, lift: 1, n: 3, speed: 1.6, size: 0.11, life: 0.5, up: 0.4, out: 5 },
    { kind: "estilhacos", when: "impacto", along: "arco", points: 5, reach: 0.95, lift: 0.6, spread: 0.8, n: 2, speed: 1.4, size: 0.1, life: 0.45, up: 0.3, out: 4 },
  ],
};

/**
 * A faixa do salto, para o pouso cair onde o corpo parou (o bote que bate num obstáculo acaba antes):
 * a origem, a frente, o comprimento e a folga (m) do atraso do estado, sem a qual o pouso de um bote inteiro
 * cairia antes do fim.
 */
export interface Lane {
  x: number;
  z: number;
  fx: number;
  fz: number;
  range: number;
  slack: number;
}

interface StepBase {
  /** A criatura (o passo de depois do preparo some se ela cambaleia, morre ou sai). */
  id: string;
  when: FxWhen;
  /** Instante (ms, relógio do cliente). */
  at: number;
  x: number;
  z: number;
  /** Altura do chão quando não é o piso do ponto (o `y` do evento: a laje da Caverna). */
  y?: number;
  color: string;
  /** No pouso do salto: a faixa (o passo anda junto com o fim dela). */
  lane?: Lane;
  /** Garantido (F93: as marcas no chão, como os avisos delas): cai mesmo se a criatura cambalear ou morrer. */
  kept?: boolean;
}

export interface ShardsStep extends StepBase {
  kind: "estilhacos";
  /** Acima do chão (m). */
  lift: number;
  n: number;
  speed: number;
  size: number;
  life: number;
  up: number;
  dx: number;
  dz: number;
  alpha: number;
  fall: boolean;
}

/** Peças da F78; o `y` de cada uma é acima do chão no ponto dela (quem dispara soma o chão). */
export interface PiecesStep extends StepBase {
  kind: "pecas";
  pieces: PieceSpec[];
}

export interface ShakeStep extends StepBase {
  kind: "tremor";
  amount: number;
  radius: number;
}

/** Onda de choque no pós (F94): `y` é acima do chão no ponto (quem dispara soma o chão). */
export interface ShockStep extends StepBase {
  kind: "choque";
  lift: number;
  strength: number;
  radius: number;
}

/** Chamas no ponto (F93): `n` lambidas espalhadas até `scatter` m. */
export interface FlamesStep extends StepBase {
  kind: "chamas";
  n: number;
  scatter: number;
  size: number;
}

export type CreatureFxStep = ShardsStep | PiecesStep | ShakeStep | FlamesStep | ShockStep;

export interface CreatureFxOptions {
  /** A cor dos efeitos (o laranja de monstro, `creatureTone`). */
  color: string;
  /** "Reduzir lampejos": sem o estalo, estilhaços mais fracos e cristais com metade do brilho. */
  reduced?: boolean;
  /** Fração dos pontos das ondas e dos anéis (com muitos efeitos vivos, menos; padrão 1). */
  density?: number;
  /** Distância da origem do ataque até a câmera (m): os cristais só até `CRYSTAL_RANGE`. */
  distance?: number;
}

/** Os cristais (uma draw call cada) só nos ataques até esta distância da câmera (m). */
export const CRYSTAL_RANGE = 25;

/** Com "Reduzir lampejos": opacidade dos estilhaços (e da onda) e o brilho dos cristais. */
export const REDUCED_FX = { shards: 0.6, glow: 0.5 } as const;

/**
 * Golpe pesado de chefe (F94), pelos dados: dos chefes, Guardiões e do Boitatá, de golpe ou de uma marca só (não o
 * arremesso, a carga, o anel vazado do chicote, nem as marcas em levas ou uma por jogador). Vale o círculo cheio e
 * o arco largo (`angle` graus ou mais) ou pesado (`damage` de dano ou mais); os sopros e cuspes em cone, estreitos e
 * leves, ficam de fora. A força cresce com o dano. Hoje: o Rochedo e a pancada do Ciclope (`mapinguari`), a Marrada
 * da Quimera, a varrida e o Lamento dos mortos do Rei Esqueleto, o salto e a área dos Guardiões e o cuspe e o bote
 * do Boitatá (o teste do `creature-fx` lista, e uma mudança de balanço aparece nele). A repetição fica pelo intervalo
 * por criatura (`SHOCK_EVERY_MS`).
 */
export const BOSS_SLAM = { angle: 180, damage: 50, lift: 0.3 } as const;

/** A força da onda de choque do golpe pesado de chefe (0 a 1); 0 quando o ataque não é um. */
export function bossSlam(archetype: CreatureArchetype | undefined, attack: CreatureAttack | undefined): number {
  if (!attack || (archetype !== "chefe" && archetype !== "guardiao" && archetype !== "final")) return 0;
  if (attack.inner !== undefined || (attack.kind !== "golpe" && attack.kind !== "marcas")) return 0;
  if (attack.kind === "marcas" && ((attack.marks?.count ?? 1) !== 1 || attack.marks?.perPlayer)) return 0;
  const heavy = attack.shape === "circulo" || (attack.shape === "arco" && (attack.angle >= BOSS_SLAM.angle || attack.damage >= BOSS_SLAM.damage));
  return heavy ? Math.min(1, 0.4 + attack.damage / 100) : 0;
}

/**
 * Intervalo mínimo entre duas ondas de choque da mesma criatura (ms): o Troll de Pedra, que não tem recarga própria
 * na pancada, e os Guardiões, que alternam o salto e a área, não enchem a tela de ondas.
 */
export const SHOCK_EVERY_MS = 4000;

/** Tem assinatura? (as outras criaturas não pagam nem o plano). */
export function hasCreatureFx(type: string, attack: string): boolean {
  return CREATURE_FX[`${type}:${attack}`] !== undefined;
}

/**
 * Os passos do ataque `e` do tipo `def` anunciado agora (`now`), em ordem de tempo, com as posições no
 * mundo tiradas da forma do aviso (`x, z, yaw, range, angle, width`; a frente é `(−sen yaw, −cos yaw)`).
 * O acerto é o do golpe sincronizado (`strikeImpactAt`: `agora + inMs − rtt/2`); o preparo começa
 * `windup` antes dele (nunca antes de agora); o pouso do salto é o acerto mais `alcance / velocidade`.
 * Vazio para quem não está na tabela.
 */
export function planCreatureFx(e: CreatureAttackEvent, def: Pick<CreatureType, "id" | "attacks"> & { archetype?: CreatureArchetype }, now: number, rtt: number, options: CreatureFxOptions): CreatureFxStep[] {
  const attack = def.attacks.find((a) => a.id === e.attack);
  const table = CREATURE_FX[`${def.id}:${e.attack}`];
  // O golpe pesado de chefe (F94) ganha a onda de choque no impacto: no círculo, no raio do aviso; no arco, no
  // meio do alcance à frente.
  const slam = bossSlam(def.archetype, attack);
  const shock: ShockRow | undefined = slam > 0 ? { kind: "choque", when: "impacto", at: attack?.shape === "arco" ? "meio" : "origem", strength: slam, reach: attack?.shape === "arco" ? 0.75 : 1, lift: BOSS_SLAM.lift } : undefined;
  const rows: readonly CreatureFxRow[] | undefined = shock ? [...(table ?? []), shock] : table;
  if (!rows || !attack) return [];
  // As marcas (F93) caem cada uma no próprio `inMs`; só a primeira move o clipe (`strikeImpactAt`).
  const impact = attack.kind === "marcas" ? now + Math.max(0, e.inMs - rtt / 2) : strikeImpactAt(attack, e, now, rtt);
  if (impact === undefined) return [];
  const kept = attack.kind === "marcas";
  const anchors = {
    preparo: Math.max(now, impact - attack.windup * 1000),
    impacto: impact,
    pouso: impact + dashMs(attack, e.range),
  };
  const fx = -Math.sin(e.yaw);
  const fz = -Math.cos(e.yaw);
  const end = { x: e.x + fx * e.range, z: e.z + fz * e.range };
  const reduced = options.reduced === true;
  const density = Math.min(1, Math.max(0, options.density ?? 1));
  // O estado chega `rtt/2` mais um patch depois do pouso previsto: a folga é o que o corpo anda nesse tempo.
  const lane: Lane | undefined = attack.kind === "salto" && attack.speed ? { x: e.x, z: e.z, fx, fz, range: e.range, slack: attack.speed * (rtt / 2000 + 0.15) + 1 } : undefined;
  const out: CreatureFxStep[] = [];
  let when: FxWhen = "impacto";
  const base = (at: number, x: number, z: number): StepBase => ({ id: e.id, when, at, x, z, ...(e.y !== undefined ? { y: e.y } : {}), color: options.color, ...(when === "pouso" && lane ? { lane } : {}), ...(kept ? { kept } : {}) });
  for (const row of rows) {
    when = row.when;
    const t0 = anchors[row.when] + (row.delayMs ?? 0);
    if (row.kind === "onda") wave(row, e, row.at === "fim" ? end : e, t0, reduced, density, base, out);
    else if (row.kind === "tremor") {
      const p = row.at === "fim" ? end : e;
      out.push({ ...base(t0, p.x, p.z), kind: "tremor", amount: row.amount, radius: row.radius });
    } else if (row.kind === "cristais") {
      if ((options.distance ?? 0) <= CRYSTAL_RANGE) out.push({ ...base(t0, e.x, e.z), kind: "pecas", pieces: crystals(row, e, reduced) });
    } else if (row.kind === "chamas") flames(row, e, t0, base, out);
    else if (row.kind === "choque") {
      const p = row.at === "fim" ? end : row.at === "meio" ? { x: e.x + (fx * e.range) / 2, z: e.z + (fz * e.range) / 2 } : e;
      out.push({ ...base(t0, p.x, p.z), kind: "choque", lift: row.lift, strength: row.strength, radius: row.radius ?? e.range * (row.reach ?? 1) });
    } else if (!(row.flash && reduced)) shards(row, e, attack, t0, end, reduced, density, base, out);
  }
  // Ordem de tempo, estável e no lugar (inserção): a fila recebe já ordenado.
  for (let i = 1; i < out.length; i++) {
    const step = out[i] as CreatureFxStep;
    let j = i;
    while (j > 0 && (out[j - 1] as CreatureFxStep).at > step.at) {
      out[j] = out[j - 1] as CreatureFxStep;
      j--;
    }
    out[j] = step;
  }
  return out;
}

/** Pontos de um anel com a densidade: pelo menos 6, e no polígono um múltiplo dos lados (a forma fica inteira). */
function ringPoints(points: number, density: number, sides = 0): number {
  const n = Math.max(6, Math.round(points * density));
  return sides >= 3 ? Math.max(sides, Math.round(n / sides) * sides) : n;
}

/** Duração do bote do salto e da carga (ms): o comprimento da faixa na velocidade do ataque. */
function dashMs(attack: CreatureAttack, range: number): number {
  return (attack.kind === "salto" || attack.kind === "carga") && attack.speed ? (range / attack.speed) * 1000 : 0;
}

function shards(row: ShardsRow, e: CreatureAttackEvent, attack: CreatureAttack, t0: number, end: { x: number; z: number }, reduced: boolean, density: number, base: (at: number, x: number, z: number) => StepBase, out: CreatureFxStep[]): void {
  const fx = -Math.sin(e.yaw);
  const fz = -Math.cos(e.yaw);
  const ring = row.along === "anel" || row.along === "fim";
  const n = row.along === "centro" ? 1 : ring && density < 1 ? Math.min(row.points, ringPoints(row.points, density)) : Math.max(1, row.points);
  const span = ((e.angle * Math.PI) / 180) * (row.spread ?? 1);
  for (let i = 0; i < n; i++) {
    const u = n === 1 ? 0.5 : i / (n - 1);
    const r = row.radius ?? e.range * (row.reach + ((row.reachEnd ?? row.reach) - row.reach) * u);
    const lift = row.lift + ((row.liftEnd ?? row.lift) - row.lift) * u;
    let x: number;
    let z: number;
    // Para fora e ao longo da varrida (unitários no chão).
    let ox: number;
    let oz: number;
    let sx: number;
    let sz: number;
    let at = t0 + i * (row.staggerMs ?? 0);
    if (row.along === "arco" || row.along === "anel" || row.along === "fim") {
      const theta = row.along === "arco" ? e.yaw + (u - 0.5) * span : e.yaw + (i / n) * Math.PI * 2;
      const c = row.along === "fim" ? end : e;
      ox = -Math.sin(theta);
      oz = -Math.cos(theta);
      x = c.x + ox * r;
      z = c.z + oz * r;
      // A tangente no sentido em que o ângulo cresce.
      sx = -Math.cos(theta);
      sz = Math.sin(theta);
    } else if (row.along === "linha") {
      const side = (row.side ?? 0) * (e.width / 2) * (i % 2 === 0 ? -1 : 1);
      // A direita da frente.
      ox = Math.cos(e.yaw) * Math.sign(side || 1);
      oz = -Math.sin(e.yaw) * Math.sign(side || 1);
      x = e.x + fx * r + Math.cos(e.yaw) * side;
      z = e.z + fz * r - Math.sin(e.yaw) * side;
      sx = fx;
      sz = fz;
      // No salto, cada ponto quando o corpo passa por ele.
      const ms = dashMs(attack, e.range);
      if (ms > 0 && e.range > 0) at = t0 + (r / e.range) * ms;
    } else {
      x = e.x + fx * r;
      z = e.z + fz * r;
      ox = fx;
      oz = fz;
      sx = 0;
      sz = 0;
    }
    const o = row.out ?? 0;
    const s = row.sweep ?? 0;
    out.push({
      ...base(at, x, z),
      kind: "estilhacos",
      lift,
      n: row.n,
      speed: row.speed,
      size: row.size,
      life: row.life,
      up: row.up ?? 0.3,
      dx: ox * o + sx * s,
      dz: oz * o + sz * s,
      alpha: reduced ? REDUCED_FX.shards : 1,
      fall: !row.flash,
    });
  }
}

/** A sequência R2 (Roberts): os pontos do sopro bem espalhados pelo cone, sem aleatório. */
const R2 = [0.7548776662466927, 0.5698402909980532] as const;

/** As chamas: no centro, num anel em volta dele ou espalhadas pelo cone do arco (o ângulo e o alcance pela R2). */
function flames(row: FlamesRow, e: CreatureAttackEvent, t0: number, base: (at: number, x: number, z: number) => StepBase, out: CreatureFxStep[]): void {
  const span = ((e.angle * Math.PI) / 180) * (row.spread ?? 1);
  const n = Math.max(1, row.points);
  for (let i = 0; i < n; i++) {
    let theta: number;
    let r: number;
    if (row.along === "arco") {
      theta = e.yaw + (((0.5 + i * R2[0]) % 1) - 0.5) * span;
      // Do perto ao longe enquanto o sopro varre, com um pouco de vaivém.
      const u = n === 1 ? 0.5 : i / (n - 1);
      const v = (0.5 + i * R2[1]) % 1;
      r = e.range * (row.reach + ((row.reachEnd ?? row.reach) - row.reach) * (0.7 * u + 0.3 * v));
    } else {
      theta = e.yaw + (i / n) * Math.PI * 2;
      r = row.along === "anel" ? e.range * row.reach : 0;
    }
    out.push({ ...base(t0 + i * (row.staggerMs ?? 0), e.x - Math.sin(theta) * r, e.z - Math.cos(theta) * r), kind: "chamas", n: row.n, scatter: row.scatter, size: row.size });
  }
}

/** Passo de referência da onda (s): o quadro de 60 Hz. */
const WAVE_STEP = 1 / 60;
/** A vida de cada estilhaço varia de 0,88 a 1× a pedida (`Shards.burst`): a onda mira a média. */
const WAVE_LIFE = 0.94;

/**
 * Velocidade (m/s) que leva um estilhaço a `distance` m na vida média dele (`WAVE_LIFE` × `life` s), com
 * o arrasto dos estilhaços como o `Shards.update` integra: a velocidade cai `1 − k·s` antes de andar, a
 * cada passo `s`. Depois de `n` passos, `x = v · (1 − k·s) · (1 − (1 − k·s)ⁿ) / k`. Medido a 60 Hz; a 30 Hz
 * a onda chega uns 4 % antes.
 */
export function waveSpeed(distance: number, life: number): number {
  const keep = 1 - SHARD_DRAG * WAVE_STEP;
  const n = (WAVE_LIFE * life) / WAVE_STEP;
  return (distance * SHARD_DRAG) / (keep * (1 - keep ** n));
}

/**
 * A onda: os pontos dão a volta no contorno (círculo, ou o polígono com um vértice à frente) e cada um sai
 * pela própria direção do contorno, então a forma abre inteira, sem deformar.
 */
function wave(row: WaveRow, e: CreatureAttackEvent, c: { x: number; z: number }, t0: number, reduced: boolean, density: number, base: (at: number, x: number, z: number) => StepBase, out: CreatureFxStep[]): void {
  const sides = WAVE_SIDES[row.shape];
  const radius = row.radius ?? e.range * (row.reach ?? 1);
  const v = waveSpeed(Math.max(0, radius - row.from), row.life);
  const points = density < 1 ? Math.min(row.points, ringPoints(row.points, density, sides)) : row.points;
  for (let i = 0; i < points; i++) {
    const t = i / points;
    let px: number;
    let pz: number;
    if (sides < 3) {
      const theta = e.yaw + t * Math.PI * 2;
      px = -Math.sin(theta);
      pz = -Math.cos(theta);
    } else {
      // Entre o vértice j e o seguinte do polígono de raio 1.
      const k = t * sides;
      const j = Math.floor(k);
      const f = k - j;
      const a0 = e.yaw + (j / sides) * Math.PI * 2;
      const a1 = e.yaw + ((j + 1) / sides) * Math.PI * 2;
      px = -Math.sin(a0) * (1 - f) - Math.sin(a1) * f;
      pz = -Math.cos(a0) * (1 - f) - Math.cos(a1) * f;
    }
    out.push({
      ...base(t0, c.x + px * row.from, c.z + pz * row.from),
      kind: "estilhacos",
      lift: row.lift,
      n: 1,
      speed: 0,
      size: row.size,
      life: row.life,
      up: 0,
      dx: px * v,
      dz: pz * v,
      alpha: reduced ? REDUCED_FX.shards : 1,
      fall: false,
    });
  }
}

/**
 * Os cristais da pancada: em volta da origem, prisma e lança alternados, inclinados para fora, saindo de
 * `depth` abaixo do chão e subindo `rise` (com saída suave), um a cada `staggerMs` (o `delayMs` das peças).
 */
function crystals(row: CrystalsRow, e: CreatureAttackEvent, reduced: boolean): PieceSpec[] {
  const r = e.range * row.reach;
  const glow = row.glow * (reduced ? REDUCED_FX.glow : 1);
  const out: PieceSpec[] = [];
  for (let i = 0; i < row.count; i++) {
    const theta = e.yaw + (i / row.count) * Math.PI * 2;
    // Um pouco fora do anel nos ímpares: a coroa não fica regular.
    const d = r * (i % 2 === 0 ? 1 : 1.12);
    const x = e.x - Math.sin(theta) * d;
    const z = e.z - Math.cos(theta) * d;
    // O +z local aponta para fora do centro.
    const yaw = theta + Math.PI;
    const common = { x, z, yaw, rise: row.rise, delayMs: i * row.staggerMs, ms: row.ms, glow, fill: row.fill };
    out.push(
      i % 2 === 0
        ? // O prisma deita em z: inclinado quase em pé, a ponta para fora.
          { ...common, shape: "prisma", y: -row.depth, scale: 1.1, length: 1.8, tilt: -(Math.PI / 2 - 0.4) }
        : { ...common, shape: "lanca", y: -row.depth - 0.3, scale: 1.5, tilt: 0.35 },
    );
  }
  return out;
}

/** Quem recebe os passos que vencem (a cena de jogo e a vitrine). */
export interface CreatureFxSink {
  fire(step: CreatureFxStep, now: number): void;
}

/** Teto da fila: cerca de 4 pancadas ao mesmo tempo (a do Colosso tem 85 passos, 56 deles da onda). */
export const CREATURE_FX_QUEUE = 384;

/** Atraso mínimo (ms) a partir do qual um passo vencido não dispara mais (aba escondida, quadro travado). */
export const STALE_MS = 250;

/**
 * Até quanto depois do instante dele o passo ainda dispara (ms): o que já teria acabado não aparece. Os
 * estilhaços pela vida, as peças pela última, o tremor por `STALE_MS`.
 */
export function staleAfter(step: CreatureFxStep): number {
  if (step.kind === "estilhacos") return Math.max(STALE_MS, step.life * 1000);
  if (step.kind === "pecas") return Math.max(STALE_MS, ...step.pieces.map((p) => (p.delayMs ?? 0) + p.ms));
  if (step.kind === "chamas") return Math.max(STALE_MS, FLAME_LIFE * 1000);
  return STALE_MS;
}

/**
 * Fila dos passos agendados, de tamanho fixo e em ordem de tempo (estável): `pushAll` na chegada do ataque,
 * `drain` a cada quadro. O quadro sem nada vencido não aloca (só compara a cabeça). Um ataque entra inteiro
 * ou fica de fora (`dropped`): sem vaga para todos os passos, nenhum entra (uma onda pela metade seria um
 * anel quebrado). O passo vencido há mais que `staleAfter` sai sem disparar (`stale`).
 */
export class CreatureFxQueue {
  private readonly slots: (CreatureFxStep | undefined)[];
  private size = 0;
  dropped = 0;
  stale = 0;

  readonly capacity: number;

  constructor(capacity = CREATURE_FX_QUEUE) {
    this.capacity = capacity;
    this.slots = new Array<CreatureFxStep | undefined>(capacity).fill(undefined);
  }

  get count(): number {
    return this.size;
  }

  /** Os passos de um ataque, todos ou nenhum; `false` quando não cabem. */
  pushAll(steps: readonly CreatureFxStep[]): boolean {
    if (this.size + steps.length > this.capacity) {
      this.dropped += steps.length;
      return false;
    }
    for (const step of steps) {
      // Depois dos de mesmo instante (estável).
      let i = this.size;
      while (i > 0 && (this.slots[i - 1] as CreatureFxStep).at > step.at) {
        this.slots[i] = this.slots[i - 1];
        i--;
      }
      this.slots[i] = step;
      this.size++;
    }
    return true;
  }

  /** Dispara em `sink` os passos com instante até `now`, na ordem, e tira da fila; os velhos saem sem disparar. */
  drain(now: number, sink: CreatureFxSink): void {
    let k = 0;
    while (k < this.size && (this.slots[k] as CreatureFxStep).at <= now) {
      const step = this.slots[k] as CreatureFxStep;
      if (now - step.at > staleAfter(step)) this.stale++;
      else sink.fire(step, now);
      k++;
    }
    if (k === 0) return;
    for (let i = k; i < this.size; i++) this.slots[i - k] = this.slots[i];
    for (let i = this.size - k; i < this.size; i++) this.slots[i] = undefined;
    this.size -= k;
  }

  clear(): void {
    for (let i = 0; i < this.size; i++) this.slots[i] = undefined;
    this.size = 0;
  }
}

/** Para onde os passos vão: o pool de estilhaços, as peças, o chão e (no jogo) o tremor e as criaturas. */
export interface CreatureFxTargets {
  /** `live`: estilhaços vivos no pool (para a densidade). */
  shards: { burst(b: ShardBurst): void; readonly live?: number };
  vfx: { spawnPieces(specs: readonly PieceSpec[], color: string, now: number, alpha?: number): void };
  ground(x: number, z: number): number;
  /** Trauma para o instante `at` de um impacto em (x, z), caindo até `radius` (a vitrine não tem). */
  shake?(at: number, x: number, z: number, amount: number, radius: number): void;
  /**
   * A criatura ainda está no ataque? (a mesma regra dos avisos: existe, não cambaleia, não morreu). Sem
   * isso (a vitrine), sempre.
   */
  active?(id: string): boolean;
  /** Onde a criatura está no estado (o último do servidor, sem interpolação): o pouso do bote barrado. */
  body?(id: string): { readonly x: number; readonly z: number } | undefined;
  /**
   * O chefe já acabou? (F93: na vitória, o servidor apaga os impactos lançados, `calm`): os passos garantidos
   * das marcas dele também somem. Sem isso, nunca.
   */
  ended?(id: string): boolean;
  /**
   * A onda de choque do pós (F94) em (x, y, z) a partir de `at` (ms), com a força e o raio final (m). Sem isso (as
   * vitrines), nada.
   */
  shock?(x: number, y: number, z: number, strength: number, radius: number, at: number): void;
  /** As chamas (F93, o pool do `fumaca`). Sem isso (a vitrine das criaturas), os passos de chama não desenham. */
  flames?: { flames(x: number, y: number, z: number, color: string, n?: number, spread?: number, size?: number, alpha?: number): void };
}

/**
 * A assinatura em andamento: a fila e o disparo de cada passo. O jorro reaproveita um objeto só (o
 * `burst` lê na hora); as peças e o tremor nascem no instante do passo, não no do quadro.
 * - Os passos de depois do preparo somem se a criatura cambaleou, morreu ou saiu (`cancelled`): o
 *   servidor cancela o ataque e o bote no cambalear. As marcas no chão já estão garantidas (`kept`), até o
 *   chefe acabar (`ended`: na vitória, o servidor apaga os impactos lançados).
 * - O pouso do salto cai onde o corpo parou, se ele parou antes do fim da faixa (bateu num obstáculo).
 * - Cada peça é uma draw call: um anel de cristais por vez (o que chega com outro vivo fica de fora,
 *   `skipped`), então são no máximo `count` draw calls a mais.
 * - A onda de choque (F94) sai no máximo uma vez a cada `SHOCK_EVERY_MS` por criatura (as outras, `throttled`).
 */
export class CreatureFxPlayer implements CreatureFxSink {
  readonly queue: CreatureFxQueue;
  private readonly targets: CreatureFxTargets;
  private readonly scratch: ShardBurst = { x: 0, y: 0, z: 0, color: "", n: 0, speed: 0, size: 0, life: 0, up: 0, alpha: 1, fall: true, dx: 0, dz: 0 };
  /** Passos disparados desde o começo (diagnóstico e e2e), os cancelados e os anéis de cristais que ficaram de fora. */
  fired = 0;
  cancelled = 0;
  skipped = 0;
  /** Ondas de choque que ficaram de fora pelo intervalo por criatura. */
  throttled = 0;
  /** A última onda de choque de cada criatura (ms). */
  private readonly shocks = new Map<string, number>();
  /** Até quando o anel de cristais vivo fica na tela (ms). */
  private piecesUntil = -Infinity;

  constructor(targets: CreatureFxTargets, capacity = CREATURE_FX_QUEUE) {
    this.targets = targets;
    this.queue = new CreatureFxQueue(capacity);
  }

  /**
   * Fração dos pontos das ondas e dos anéis para o próximo ataque: a metade com o pool de estilhaços ou a
   * fila passando da metade (as ondas que se cruzam não comem os pontos umas das outras no pool).
   */
  density(): number {
    return (this.targets.shards.live ?? 0) > SHARD_POOL / 2 || this.queue.count > this.queue.capacity / 2 ? 0.5 : 1;
  }

  play(steps: readonly CreatureFxStep[]): boolean {
    return this.queue.pushAll(steps);
  }

  update(now: number): void {
    this.queue.drain(now, this);
  }

  fire(step: CreatureFxStep, _now: number): void {
    const t = this.targets;
    if ((step.when !== "preparo" && !step.kept && t.active && !t.active(step.id)) || (step.kept && t.ended?.(step.id))) {
      this.cancelled++;
      return;
    }
    if (step.kind === "choque" && !this.shockReady(step.id, step.at)) {
      this.throttled++;
      return;
    }
    this.fired++;
    // Bote barrado: o pouso anda com o fim da faixa até onde o corpo está.
    let x = step.x;
    let z = step.z;
    const lane = step.lane;
    const body = lane && t.body?.(step.id);
    if (lane && body) {
      const along = Math.max(0, Math.min(lane.range, (body.x - lane.x) * lane.fx + (body.z - lane.z) * lane.fz));
      if (along < lane.range - lane.slack) {
        x += lane.fx * (along - lane.range);
        z += lane.fz * (along - lane.range);
      }
    }
    switch (step.kind) {
      case "estilhacos": {
        const b = this.scratch;
        b.x = x;
        b.y = (step.y ?? t.ground(x, z)) + step.lift;
        b.z = z;
        b.color = step.color;
        b.n = step.n;
        b.speed = step.speed;
        b.size = step.size;
        b.life = step.life;
        b.up = step.up;
        b.alpha = step.alpha;
        b.fall = step.fall;
        b.dx = step.dx;
        b.dz = step.dz;
        t.shards.burst(b);
        return;
      }
      case "pecas":
        if (step.at < this.piecesUntil) {
          this.skipped++;
          return;
        }
        this.piecesUntil = step.at + Math.max(0, ...step.pieces.map((p) => (p.delayMs ?? 0) + p.ms));
        t.vfx.spawnPieces(
          step.pieces.map((p) => ({ ...p, y: p.y + (step.y ?? t.ground(p.x, p.z)) })),
          step.color,
          step.at,
        );
        return;
      case "tremor":
        t.shake?.(step.at, x, z, step.amount, step.radius);
        return;
      case "chamas":
        t.flames?.flames(x, (step.y ?? t.ground(x, z)) + 0.02, z, step.color, step.n, step.scatter, step.size);
        return;
      case "choque":
        t.shock?.(x, (step.y ?? t.ground(x, z)) + step.lift, z, step.strength, step.radius, step.at);
        return;
    }
  }

  clear(): void {
    this.queue.clear();
    this.piecesUntil = -Infinity;
    this.shocks.clear();
  }

  /** A criatura pode soltar outra onda de choque em `at`? Se pode, marca. As marcas velhas saem de vez em quando. */
  private shockReady(id: string, at: number): boolean {
    const last = this.shocks.get(id);
    if (last !== undefined && at - last < SHOCK_EVERY_MS) return false;
    if (this.shocks.size >= 32) for (const [k, t] of this.shocks) if (at - t >= SHOCK_EVERY_MS) this.shocks.delete(k);
    this.shocks.set(id, at);
    return true;
  }
}
